import { QueryClient } from '@tanstack/react-query'
import { httpBatchLink, httpLink, splitLink, unstable_httpSubscriptionLink } from '@trpc/client'
import { createTRPCQueryUtils, createTRPCReact, type inferReactQueryProcedureOptions } from '@trpc/react-query'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'
import { v4 as uuidv4 } from 'uuid'
import type { AppRouter } from '../../../../src/server'

export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>
export type RouterInputs = inferRouterInputs<AppRouter>
export type RouterOutputs = inferRouterOutputs<AppRouter>

export const trpc = createTRPCReact<AppRouter>()

const publicPages: Array<string> = [
  '/share/templates',
  '/share/inspiration',
  '/reactivation',
  '/reactivation?plan=spark-plan-no-trial-usd-Monthly',
  '/reactivation?plan=creativeos-standard-no-trial',
  '/reactivation?plan=creativeos-pro-no-trial',
  '/funnel-analyzer',
  '/service-down',
]

const UNAUTHORIZED_STATUS = 401
const SERVER_ERROR_STATUS = 500

const LOGIN_PATH = '/login?redirect=true'

const clearAuthData = () => {
  localStorage.removeItem('_wos_user')
  document.body.classList.remove('dark')
}

export const cFetch = async (url: RequestInfo | URL, options?: RequestInit) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    })

    const currentPath = window.location.pathname
    const isPublicPage = publicPages.some((page) => currentPath.startsWith(page))

    if (response.status === UNAUTHORIZED_STATUS) {
      if (!isPublicPage) {
        clearAuthData()
        window.location.href = LOGIN_PATH
      }
    }
    if (response.status === SERVER_ERROR_STATUS) {
      const error = await response.json()
      if (error?.error?.message?.includes('invalid_grant') && !isPublicPage) {
        clearAuthData()
        window.location.href = LOGIN_PATH
      }
    }

    return response
  } catch (error) {
    console.error('Fetch error:', error)
    throw error
  }
}

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition(op) {
        return op.type === 'subscription' // Direct subscriptions to SSE
      },
      true: unstable_httpSubscriptionLink({
        url: import.meta.env.VITE_API_URL as string, // SSE URL
        eventSourceOptions() {
          return {
            withCredentials: true, // <---
          }
        },
      }),
      false: splitLink({
        condition(op) {
          // Batch only specific procedures
          return op.context.batch || false
        },
        true: httpBatchLink({
          url: import.meta.env.VITE_API_URL as string,
          fetch: cFetch,
          headers(opts) {
            return {
              'X-Request-Id': uuidv4(),
              ...(opts.opList[0]?.context.idempotent ? { idempotent: 'true' } : {}),
            }
          },
        }),
        false: httpLink({
          url: import.meta.env.VITE_API_URL as string,
          fetch: cFetch,
          headers(opts) {
            return {
              'X-Request-Id': uuidv4(),
              ...(opts.op.context.idempotent ? { idempotent: 'true' } : {}),
            }
          },
        }),
      }),
    }),
  ],
})

export const queryClient = new QueryClient()
export const clientUtils = createTRPCQueryUtils({ queryClient, client: trpcClient })
